import type { PaymentMethod, ReturnableItem, ReturnProjection, ReturnType } from "./api";
import { cleanIntegerInput, formatIntegerInput, formatQuantity } from "./numberFormat";
import { WORK_DRAFT_TTL_MS } from "./workDraft";

export type RefundLine = { method: PaymentMethod; amount: string };

export function cleanDecimal(value: string, places = 2): string {
  const cleaned = value.replace(/\s/g, "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  const [whole, ...fraction] = cleaned.split(".");
  return fraction.length ? `${whole}.${fraction.join("").slice(0, places)}` : whole;
}

export function cents(value: string): bigint | null {
  if (!/^\d+(\.\d{0,2})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  if (whole.length > 12) return null;
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
}

export function amount(value: bigint): string {
  return `${value / 100n}.${(value % 100n).toString().padStart(2, "0")}`;
}

function milli(value: string): bigint {
  if (!/^\d+(\.\d{0,3})?$/.test(value)) return 0n;
  const [whole, fraction = ""] = value.split(".");
  return BigInt(whole) * 1000n + BigInt(fraction.padEnd(3, "0"));
}

export function lineAmount(item: ReturnableItem, quantity: string): bigint {
  const selected = milli(quantity), original = milli(item.original_quantity);
  if (selected === 0n || original === 0n) return 0n;
  const priorQuantity = milli(item.returned_quantity);
  const cumulative = priorQuantity + selected < original ? priorQuantity + selected : original;
  const sourceValue = cents(item.original_value) ?? 0n, priorValue = cents(item.returned_value) ?? 0n;
  // Half-up cumulative allocation matches backend Decimal, including the final cent.
  const target = (2n * sourceValue * cumulative + original) / (2n * original);
  const remaining = sourceValue - priorValue, difference = target - priorValue;
  const result = difference < remaining ? difference : remaining;
  return result > 0n ? result : 0n;
}

export function sameReturnBasis(a: ReturnableItem, b: ReturnableItem): boolean {
  return a.product_id === b.product_id
    && milli(a.original_quantity) === milli(b.original_quantity)
    && milli(a.returned_quantity) === milli(b.returned_quantity)
    && milli(a.returnable_max) === milli(b.returnable_max)
    && cents(a.original_value) === cents(b.original_value)
    && cents(a.returned_value) === cents(b.returned_value);
}

export function refundError(lines: RefundLine[], refund: string): "required" | "amount" | "total" | null {
  const target = cents(refund);
  if (target === null) return "amount";
  if (target === 0n) return lines.length ? "total" : null;
  if (!lines.length) return "required";
  const values = lines.map((line) => cents(line.amount));
  if (values.some((value) => value === null || value <= 0n)) return "amount";
  return values.reduce<bigint>((sum, value) => sum + value!, 0n) === target ? null : "total";
}


/** Whole-unit presentation only. Exact cents remain in form state and payloads. */
export function returnMoneyInput(value: string): string {
  const parsed = cents(value);
  return parsed === null ? "" : formatIntegerInput(String(parsed / 100n));
}

export function editReturnMoney(raw: string, previous: string): string {
  const digits = cleanIntegerInput(raw.split(/[.,]/)[0]).slice(0, 12);
  if (!digits) return "";
  const whole = BigInt(digits);
  // An explicit zero really clears the amount; other edits retain its cents.
  return amount(whole * 100n + (whole === 0n ? 0n : (cents(previous) ?? 0n) % 100n));
}

/** The final selected method carries the refund's hidden fractional remainder. */
export function wholeRefundLines(lines: RefundLine[], refund: string): RefundLine[] {
  const tail = (cents(refund) ?? 0n) % 100n;
  return lines.map((line, index) => {
    const value = cents(line.amount);
    if (value === null || value === 0n) return line;
    return { ...line, amount: amount(value / 100n * 100n + (index === lines.length - 1 ? tail : 0n)) };
  });
}

export const RETURN_DRAFT_TTL_MS = WORK_DRAFT_TTL_MS;
export const RETURN_DRAFT_PREFIX = "avtodaftar_mechanic_return_draft_v1:";
export type ReturnDraft = {
  version: 1; userId: number; workshopId: number; returnType: ReturnType; sourceId: number;
  createdAt: number; operationUuid: string; quantities: Record<number, string>; all: boolean;
  debt: string; cash: string; payments: RefundLine[]; reason: string;
  basis: ReturnProjection; dirty: boolean; needsReview: boolean;
};
export function returnDraftKey(userId: number, workshopId: number, type: ReturnType, sourceId: number): string {
  return `${RETURN_DRAFT_PREFIX}${workshopId}:${userId}:${type}:${sourceId}`;
}

export function readReturnDraft(userId: number, workshopId: number, type: ReturnType, sourceId: number, now = Date.now()): ReturnDraft | null {
  const key = returnDraftKey(userId, workshopId, type, sourceId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as ReturnDraft;
    const decimal = (v: unknown, places: number) => typeof v === "string" && v.length <= 16
      && (v === "" || new RegExp(`^\\d+(\\.\\d{0,${places}})?$`).test(v));
    const basis = value.basis;
    const valid = value.version === 1 && value.userId === userId && value.workshopId === workshopId
      && value.returnType === type && value.sourceId === sourceId && Number.isFinite(value.createdAt)
      && value.createdAt <= now && typeof value.operationUuid === "string" && value.operationUuid.length > 0
      && typeof value.all === "boolean" && typeof value.dirty === "boolean" && typeof value.needsReview === "boolean"
      && typeof value.reason === "string" && decimal(value.debt, 2) && decimal(value.cash, 2)
      && value.quantities && typeof value.quantities === "object" && !Array.isArray(value.quantities)
      && Object.entries(value.quantities).every(([id, qty]) => /^\d+$/.test(id) && Number(id) > 0 && decimal(qty, 3))
      && Array.isArray(value.payments) && new Set(value.payments.map(line => line.method)).size === value.payments.length
      && value.payments.every(line => ["naqd", "terminal", "karta_otkazma", "bank"].includes(line.method) && decimal(line.amount, 2))
      && basis?.return_type === type && basis.original_document_id === sourceId && cents(basis.debt_reduction_max) !== null
      && Array.isArray(basis.items) && new Set(basis.items.map(item => item.product_id)).size === basis.items.length
      && basis.items.every(item => Number.isInteger(item.product_id) && item.product_id > 0
        && decimal(item.original_quantity, 3) && decimal(item.returned_quantity, 3) && decimal(item.returnable_max, 3)
        && cents(item.original_value) !== null && cents(item.returned_value) !== null);
    if (!valid || now >= value.createdAt + RETURN_DRAFT_TTL_MS) { localStorage.removeItem(key); return null; }
    return value;
  } catch {
    try { localStorage.removeItem(key); } catch { /* device storage is unavailable */ }
    return null;
  }
}

export function writeReturnDraft(draft: ReturnDraft): boolean {
  try { localStorage.setItem(returnDraftKey(draft.userId, draft.workshopId, draft.returnType, draft.sourceId), JSON.stringify(draft)); return true; }
  catch { return false; }
}
export function clearReturnDraft(userId: number, workshopId: number, type: ReturnType, sourceId: number, uuid?: string): void {
  const key = returnDraftKey(userId, workshopId, type, sourceId);
  try {
    const raw = localStorage.getItem(key);
    if (!uuid || !raw || (JSON.parse(raw) as ReturnDraft).operationUuid === uuid) localStorage.removeItem(key);
  } catch { /* in-memory state remains usable */ }
}
/** Ordinary drafts are private to the account; frozen writes keep their retry identity. */
export function clearReturnDraftsForUser(userId: number, workshopId: number, pendingUuid?: string): void {
  try {
    const prefix = `${RETURN_DRAFT_PREFIX}${workshopId}:${userId}:`;
    const keys = Object.keys(localStorage).filter(key => key.startsWith(prefix));
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!pendingUuid || !raw || (JSON.parse(raw) as ReturnDraft).operationUuid !== pendingUuid) localStorage.removeItem(key);
    }
  } catch { /* device storage is unavailable */ }
}

export function returnBasisChanged(a: ReturnProjection, b: ReturnProjection): boolean {
  return a.return_type !== b.return_type || a.original_document_id !== b.original_document_id || a.branch_id !== b.branch_id
    || cents(a.debt_reduction_max) !== cents(b.debt_reduction_max) || a.items.length !== b.items.length
    || b.items.some(item => { const old = a.items.find(line => line.product_id === item.product_id); return !old || !sameReturnBasis(old, item); });
}
export function returnDraftTotal(draft: ReturnDraft): bigint {
  return draft.basis.items.reduce((sum, item) => sum + lineAmount(item, draft.quantities[item.product_id] ?? "0"), 0n);
}

/** Refresh metadata without overwriting edits. Changed limits require explicit review. */
export function reconcileReturnDraft(draft: ReturnDraft, basis: ReturnProjection, forceReview = false): ReturnDraft {
  if (!forceReview && !returnBasisChanged(draft.basis, basis)) {
    return cents(basis.debt_reduction_max) === 0n
      ? { ...draft, basis, debt: "0.00" } : { ...draft, basis };
  }
  const quantities = Object.fromEntries(basis.items.map(item => {
    const previous = draft.quantities[item.product_id] ?? "0";
    const quantity = draft.all || milli(previous) > milli(item.returnable_max) ? formatQuantity(item.returnable_max) : previous;
    return [item.product_id, quantity];
  }));
  const next = { ...draft, basis, quantities, needsReview: true };
  const total = returnDraftTotal(next), oldTotal = returnDraftTotal(draft);
  const debt = cents(draft.debt), cap = cents(basis.debt_reduction_max) ?? 0n;
  if (cap === 0n) { next.debt = "0.00"; next.cash = amount(total); }
  else if (debt !== null && (total !== oldTotal || debt > cap || debt > total)) {
    const bounded = debt < cap ? debt : cap;
    const reduction = bounded < total ? bounded : total;
    next.debt = amount(reduction);
    if (cents(draft.cash) !== null) next.cash = amount(total - reduction);
  }
  return next;
}
